import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, ArrowLeft, MapPin, Cpu, Plus, Building2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import MetaCard from "@/components/MetaCard";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate } from "@/lib/format";
import { readBack, useWithBack, segmentBackOverride } from "@/lib/nav";

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [site, setSite] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const withBackHref = useWithBack();
  const activeTab = searchParams.get("tab") || "details";
  const setTab = (v) => {
    // Guard against Radix TabsTrigger firing onValueChange twice per click:
    // React state is stale on the second call; window.location.search is not.
    const current = new URLSearchParams(window.location.search).get("tab") || "details";
    if (v === current) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: false });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: mreq }] = await Promise.all([
        api.get(`/sites/${id}`),
        api.get(`/sites/${id}/machines`),
      ]);
      setSite(s);
      setMachines(mreq);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/sites", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !site) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="site-detail-page">
      <Breadcrumbs
        items={[
          { label: "Customers", to: segmentBackOverride("/customers", backCtx?.to) },
          ...(site.customer
            ? [
                {
                  label: site.customer.name,
                  to: segmentBackOverride(`/customers/${site.customer.id}`, backCtx?.to),
                },
                {
                  label: "Sites",
                  to: segmentBackOverride(`/customers/${site.customer.id}?tab=sites`, backCtx?.to),
                },
              ]
            : [{ label: "Sites", to: segmentBackOverride("/sites", backCtx?.to) }]),
          { label: site.name },
        ]}
      />
      <PageHeader
        testId="site-detail-header"
        title={site.name}
        subtitle={
          <span className="flex items-center gap-2">
            {site.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
            {site.customer && (
              <Link to={`/customers/${site.customer.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
                <Building2 className="h-3 w-3" /> {site.customer.name}
              </Link>
            )}
          </span>
        }
        actions={<>
          <Button asChild variant="outline" size="sm" data-testid="site-detail-back"><Link to={backCtx?.to || "/sites"}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
          {hasPermission("site.write") && (
            <Button asChild size="sm" data-testid="site-detail-edit"><Link to={`/sites/${site.id}/edit`}><Pencil className="h-4 w-4" /> Edit</Link></Button>
          )}
        </>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setTab}>
            <TabsList className="rounded-lg bg-muted p-1">
              <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
              <TabsTrigger value="machines" data-testid="site-tab-machines">Machines ({machines.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4 space-y-6" data-testid="tab-content-details">
              <Card className="rounded-md">
                <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> On-site contact</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Field label="Name" value={site.on_site_contact_name} />
                  <Field label="Phone" value={site.on_site_contact_phone} />
                  <Field label="Email" value={site.on_site_contact_email} />
                  <div />
                </CardContent>
              </Card>

              <Card className="rounded-md">
                <CardHeader><CardTitle className="text-sm font-medium">Address</CardTitle></CardHeader>
                <CardContent className="text-sm leading-relaxed">
                  <div>{site.address?.street || "—"}</div>
                  <div>{[site.address?.city, site.address?.postcode].filter(Boolean).join(", ") || "—"}</div>
                  <div>{site.address?.country}</div>
                </CardContent>
              </Card>

              {site.access_notes && (
                <Card className="rounded-md">
                  <CardHeader><CardTitle className="text-sm font-medium">Access notes</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">{site.access_notes}</CardContent>
                </Card>
              )}
              {site.notes && (
                <Card className="rounded-md">
                  <CardHeader><CardTitle className="text-sm font-medium">Notes</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">{site.notes}</CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="machines" className="mt-4">
              <Card className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> Machines</CardTitle>
                  {hasPermission("machine.write") && (
                    <Button asChild size="sm" variant="outline" data-testid="site-detail-new-machine">
                      <Link to={`/machines/new?site_id=${site.id}&back=${encodeURIComponent(`/sites/${site.id}?tab=machines`)}`}><Plus className="h-3.5 w-3.5" /> Add machine</Link>
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {machines.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground" data-testid="site-machines-empty">No machines installed yet.</div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {machines.map((m) => (
                        <li key={m.id} className="p-3 flex items-center justify-between hover:bg-muted/30" data-testid={`site-machine-${m.id}`}>
                          <div>
                            <Link to={withBackHref(`/machines/${m.id}`)} className="text-sm font-medium hover:text-primary" data-testid={`site-machine-link-${m.id}`}>{m.make} {m.model}</Link>
                            <div className="text-xs text-muted-foreground">Serial {m.serial}</div>
                          </div>
                          {m.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <MetaCard
            title="Metadata"
            fields={[
              { label: "Status", value: site.active ? "Active" : "Inactive" },
              { label: "Machines", value: site.machine_count, testId: "site-meta-machine-count" },
              { label: "Created", value: formatDate(site.created_at) },
              { label: "Updated", value: formatDate(site.updated_at) },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}
